/* Evaluating Frappe's `depends_on`, without handing a browser `eval`.
 *
 * Frappe writes these as `eval:doc.status == "Draft" && doc.total > 0`, and
 * ERPNext ships thousands of them. They decide whether a field is shown,
 * required or read-only, so getting them wrong shows somebody a field that
 * should be hidden -- or, worse, hides one they must fill in.
 *
 * The obvious implementation is `new Function('doc', ...)`. That is arbitrary
 * code execution driven by a string that an administrator (or anyone who can
 * write to the DocType) controls, running in the signed-in person's browser.
 * So this is a real parser for a small language instead:
 *
 *   values      "text"  'text'  12  1.5  true  false  null
 *   references  doc.field   doc.field.length   frappe.boot.x (treated as null)
 *   operators   == === != !== < <= > >=  && || !  + - * /  in  not in
 *   grouping    ( )  [ a, b ]
 *
 * Anything it cannot parse is **logged and treated as "show the field"**,
 * which is the design's rule (§12): a field wrongly shown is a puzzle, a
 * field wrongly hidden is data nobody can enter.
 */

type Node =
  | { k: 'lit'; v: unknown }
  | { k: 'ref'; path: string[] }
  | { k: 'arr'; items: Node[] }
  | { k: 'un'; op: string; a: Node }
  | { k: 'call'; fn: string; args: Node[] }
  | { k: 'method'; on: Node; name: string; args: Node[] }
  | { k: 'bin'; op: string; a: Node; b: Node }

const NUM = /^\d+(\.\d+)?/
const NAME = /^[A-Za-z_$][A-Za-z0-9_$]*/

class Fail extends Error {}

function lex(src: string): string[] {
  const out: string[] = []
  let i = 0
  while (i < src.length) {
    const rest = src.slice(i)
    const ch = src[i]!
    if (/\s/.test(ch)) { i++; continue }
    if (ch === '"' || ch === "'") {
      const end = src.indexOf(ch, i + 1)
      if (end < 0) throw new Fail('unterminated string')
      out.push(src.slice(i, end + 1)); i = end + 1; continue
    }
    const three = rest.slice(0, 3)
    if (three === '===' || three === '!==') { out.push(three); i += 3; continue }
    const two = rest.slice(0, 2)
    // `doc.links?.length` -- optional chaining is already how this evaluator
    // walks a path (a missing step yields undefined), so the `?` is noise.
    if (two === '?.') { out.push('.'); i += 2; continue }
    if (['==', '!=', '<=', '>=', '&&', '||'].includes(two)) { out.push(two); i += 2; continue }
    if ('()[],<>!+-*/.'.includes(ch)) { out.push(ch); i++; continue }
    const num = NUM.exec(rest)
    if (num) { out.push(num[0]); i += num[0].length; continue }
    const name = NAME.exec(rest)
    if (name) { out.push(name[0]); i += name[0].length; continue }
    throw new Fail(`unexpected ${ch}`)
  }
  return out
}

/** The only functions an expression may call. Frappe's own, reimplemented --
 *  never looked up on any object, so nothing else is reachable. */
const HELPERS: Record<string, (args: unknown[]) => unknown> = {
  in_list: ([list, value]) => Array.isArray(list) && list.some((x) => looseEq(x, value)),
  cint: ([v]) => Math.trunc(Number(v) || 0),
  flt: ([v]) => Number(v) || 0,
  cstr: ([v]) => (v == null ? '' : String(v)),
  len: ([v]) => (Array.isArray(v) || typeof v === 'string' ? v.length : 0),
}

/** Methods an expression may call on a value. ERPNext writes
 *  `["Road","Ship"].includes(doc.mode_of_transport)` and
 *  `[...].indexOf(doc.charge_type) !== -1` interchangeably. As with HELPERS
 *  these are reimplemented, never dispatched to the real object, so nothing
 *  beyond this table is reachable. */
const METHODS: Record<string, (target: unknown, args: unknown[]) => unknown> = {
  includes: (target, [value]) =>
    Array.isArray(target) ? target.some((x) => looseEq(x, value))
      : typeof target === 'string' ? target.includes(String(value ?? '')) : false,
  indexOf: (target, [value]) =>
    Array.isArray(target) ? target.findIndex((x) => looseEq(x, value))
      : typeof target === 'string' ? target.indexOf(String(value ?? '')) : -1,
  trim: (target) => String(target ?? '').trim(),
  toLowerCase: (target) => String(target ?? '').toLowerCase(),
  toUpperCase: (target) => String(target ?? '').toUpperCase(),
}

const BINDING: Record<string, number> = {
  '||': 1, '&&': 2,
  '==': 3, '!=': 3, '===': 3, '!==': 3, 'in': 3,
  '<': 4, '<=': 4, '>': 4, '>=': 4,
  '+': 5, '-': 5, '*': 6, '/': 6,
}

function parse(tokens: string[]): Node {
  let at = 0
  const peek = () => tokens[at]
  const take = () => tokens[at++]

  function primary(): Node {
    return postfix(atom())
  }

  /** `.includes(x)`, `.indexOf(x)` and further `.field` steps after any
   *  value -- including after an array literal, which a `ref` path cannot
   *  express. */
  function postfix(node: Node): Node {
    for (;;) {
      if (peek() !== '.') return node
      take()
      const name = take()
      if (!name) throw new Fail('expected a name after .')
      if (peek() === '(') {
        take()
        const args: Node[] = []
        if (peek() !== ')') {
          for (;;) { args.push(expr(0)); if (peek() === ',') { take(); continue } break }
        }
        if (take() !== ')') throw new Fail('expected )')
        if (!(name in METHODS)) throw new Fail(`unknown method ${name}`)
        node = { k: 'method', on: node, name, args }
        continue
      }
      node = node.k === 'ref'
        ? { k: 'ref', path: [...node.path, name] }
        : { k: 'method', on: node, name: `.${name}`, args: [] }
    }
  }

  function atom(): Node {
    const t = take()
    if (t === undefined) throw new Fail('ran out')
    if (t === '!') return { k: 'un', op: '!', a: primary() }
    if (t === '-') return { k: 'un', op: '-', a: primary() }
    if (t === '(') { const inner = expr(0); if (take() !== ')') throw new Fail('expected )'); return inner }
    if (t === '[') {
      const items: Node[] = []
      if (peek() !== ']') {
        for (;;) { items.push(expr(0)); if (peek() === ',') { take(); continue } break }
      }
      if (take() !== ']') throw new Fail('expected ]')
      return { k: 'arr', items }
    }
    if (t[0] === '"' || t[0] === "'") return { k: 'lit', v: t.slice(1, -1) }
    if (NUM.test(t) && /^[\d.]+$/.test(t)) return { k: 'lit', v: Number(t) }
    if (t === 'true') return { k: 'lit', v: true }
    if (t === 'false') return { k: 'lit', v: false }
    if (t === 'null' || t === 'undefined') return { k: 'lit', v: null }
    if (NAME.test(t)) {
      // A Frappe helper: `in_list(["a","b"], doc.x)` is common enough in
      // ERPNext that not supporting it cost 10% of all expressions.
      if (peek() === '(') {
        take()
        const args: Node[] = []
        if (peek() !== ')') {
          for (;;) { args.push(expr(0)); if (peek() === ',') { take(); continue } break }
        }
        if (take() !== ')') throw new Fail('expected )')
        if (!(t in HELPERS)) throw new Fail(`unknown helper ${t}`)
        return { k: 'call', fn: t, args }
      }
      return { k: 'ref', path: [t] }
    }
    throw new Fail(`unexpected ${t}`)
  }

  function expr(min: number): Node {
    let left = primary()
    for (;;) {
      let op = peek()
      if (op === 'not' && tokens[at + 1] === 'in') { take(); take(); const right = expr(4); left = { k: 'un', op: '!', a: { k: 'bin', op: 'in', a: left, b: right } }; continue }
      if (!op || !(op in BINDING)) break
      const power = BINDING[op]!
      if (power < min) break
      take()
      const right = expr(power + 1)
      left = { k: 'bin', op, a: left, b: right }
    }
    return left
  }

  const tree = expr(0)
  if (at !== tokens.length) throw new Fail(`trailing ${tokens[at]}`)
  return tree
}

function run(node: Node, doc: Record<string, unknown>): unknown {
  switch (node.k) {
    case 'lit': return node.v
    case 'arr': return node.items.map((i) => run(i, doc))
    case 'ref': {
      const [head, ...rest] = node.path
      // `doc.x`, and a bare `x` which Frappe also allows.
      let value: unknown = head === 'doc' ? doc : head === 'frappe' ? undefined : doc[head!]
      for (const part of rest) {
        if (value == null) return undefined
        value = (value as Record<string, unknown>)[part]
      }
      return value
    }
    case 'call': {
      const fn = HELPERS[node.fn]
      if (!fn) throw new Fail(`unknown helper ${node.fn}`)
      return fn(node.args.map((a) => run(a, doc)))
    }
    case 'method': {
      const target = run(node.on, doc)
      // A bare `.name` that is not a call: a property read on a value, which
      // only `length` is ever actually used for.
      if (node.name.startsWith('.')) {
        const key = node.name.slice(1)
        if (target == null) return undefined
        if (key === 'length') return Array.isArray(target) || typeof target === 'string' ? target.length : 0
        return (target as Record<string, unknown>)[key]
      }
      const fn = METHODS[node.name]
      if (!fn) throw new Fail(`unknown method ${node.name}`)
      return fn(target, node.args.map((a) => run(a, doc)))
    }
    case 'un': {
      const a = run(node.a, doc)
      return node.op === '!' ? !truthy(a) : -(Number(a) || 0)
    }
    case 'bin': {
      const a = run(node.a, doc)
      if (node.op === '&&') return truthy(a) ? run(node.b, doc) : a
      if (node.op === '||') return truthy(a) ? a : run(node.b, doc)
      const b = run(node.b, doc)
      switch (node.op) {
        // Loose on purpose: Frappe stores a Check as 0/1 and compares it with
        // `== 1`, and a Select's value against a string, in the same file.
        case '==': case '===': return looseEq(a, b)
        case '!=': case '!==': return !looseEq(a, b)
        case '<': return Number(a) < Number(b)
        case '<=': return Number(a) <= Number(b)
        case '>': return Number(a) > Number(b)
        case '>=': return Number(a) >= Number(b)
        case '+': return typeof a === 'string' || typeof b === 'string' ? `${a ?? ''}${b ?? ''}` : Number(a) + Number(b)
        case '-': return Number(a) - Number(b)
        case '*': return Number(a) * Number(b)
        case '/': return Number(b) === 0 ? 0 : Number(a) / Number(b)
        case 'in': return Array.isArray(b) ? b.some((x) => looseEq(x, a))
          : typeof b === 'string' ? b.includes(String(a)) : false
        default: throw new Fail(`operator ${node.op}`)
      }
    }
  }
}

function looseEq(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  if (a == null || b == null) return (a ?? 0) === (b ?? 0) || (!a && !b)
  if (typeof a === 'number' || typeof b === 'number') return Number(a) === Number(b)
  if (typeof a === 'boolean' || typeof b === 'boolean') return Boolean(a) === Boolean(b)
  return String(a) === String(b)
}

const truthy = (v: unknown) => !(v === undefined || v === null || v === '' || v === 0 || v === false || v === '0')

const cache = new Map<string, Node | null>()
const complained = new Set<string>()

/**
 * Evaluate one `depends_on` against a document.
 *
 * Returns `fallback` (true) when the condition is empty or cannot be parsed,
 * so an expression we do not understand shows the field rather than hiding it.
 */
export function evaluateCondition(condition: string | undefined, doc: Record<string, unknown>, fallback = true): boolean {
  if (!condition) return fallback
  const src = condition.trim()
  if (!src) return fallback

  // Frappe also allows a bare fieldname, meaning "when this has a value".
  if (!src.startsWith('eval:')) {
    if (NAME.test(src) && src === NAME.exec(src)?.[0]) return truthy(doc[src])
    return fallback
  }

  let tree = cache.get(src)
  if (tree === undefined) {
    try {
      // ERPNext ships a few with a trailing `;`, copied out of a script.
      tree = parse(lex(src.slice(5).trim().replace(/;+\s*$/, '')))
    } catch (err) {
      tree = null
      if (!complained.has(src)) {
        complained.add(src)
        // Logged, not swallowed: the design says an unsupported expression is
        // reported so the list of what to support next is real.
        console.warn('[erp-kit] cannot read a depends_on, showing the field:', src, String(err))
      }
    }
    cache.set(src, tree)
  }
  if (tree === null) return fallback
  try {
    return truthy(run(tree, doc))
  } catch {
    return fallback
  }
}

/** For tests and for the report of what the engine could not read. */
export function unsupportedConditions(): string[] { return [...complained] }
